import React, { useEffect, useState, useContext } from "react";
import {
  DetailsList,
  IGroup,
  IDetailsHeaderProps,
  DetailsHeader,
  IColumn,
  IRenderFunction,
  Text
} from "@fluentui/react";

import { ISLGetResults } from "../../services/TypeUtil";

import { css } from "@emotion/css";
import { SLCreateSLBatchContext } from "../../contexts/SLCreateSLBatchContext";
import { SLLoadingStatus } from "../../utilities/utilities";
import LoadingSpinner from "../../baseComponents/LoadingSpinner";
import { ISLBatchResponse } from "./SLBatchSchemas";

const headerStyles = css`
  & .batch-file-input {
    padding-bottom: 8px;
  }
  & .batch-description-margin {
    margin-left: 8px;
  }
  & .batch-browse-button {
    float: right;
  }
  & .title-color {
    color: black;
  }
`;

export interface ISLBatchValidationErrorsGetter extends ISLGetResults {
  result: ISLBatchValidationErrors;
}

export interface ISLBatchValidationErrors {
  errors: ErrorUnit[] | undefined;
}

interface IDetailsListGroupedLargeExampleItem {
  key: string;
  name: string;
  value: string;
}

export interface ErrorUnit {
  excelLineNumber: string;
  errorMsgs: string[];
}

interface IValidationErrorsState {
  columns: IColumn[];
  messages: IDetailsListGroupedLargeExampleItem[];
  groups: IGroup[];
}

const headerFormat: IRenderFunction<IDetailsHeaderProps> = (props) => {
  return <DetailsHeader {...props} layoutMode={1} ariaLabelForToggleAllGroupsButton={"Toggle selection"} />;
};

const propToValidationErrors = (errorMessages: ErrorUnit[]): IValidationErrorsState => {
  const columns = [
    { key: "name", name: "Excel Row Number", fieldName: "name", minWidth: 100, maxWidth: 200, isResizable: true },
    { key: "value", name: "Error Message", fieldName: "value", minWidth: 100, maxWidth: 200, isResizable: true }
  ];

  const messages: IDetailsListGroupedLargeExampleItem[] = [];

  errorMessages.forEach((element) => {
    element.errorMsgs.forEach((mess) => {
      messages.push({
        key: element.excelLineNumber,
        name: element.excelLineNumber,
        value: mess
      });
    });
  });

  const groups: IGroup[] = [];
  errorMessages.forEach((element) => {
    groups.push({
      key: element.excelLineNumber,
      name: "Row " + element.excelLineNumber,
      startIndex: messages.findIndex((x) => x.key === element.excelLineNumber),
      count: element.errorMsgs.length,
      level: 0
    });
  });

  return {
    columns,
    messages,
    groups
  };
};

const initialState = propToValidationErrors([]);

const SLBatchValidationErrors: React.FC = () => {
  const entryContext = useContext(SLCreateSLBatchContext);
  const [state, setState] = useState<IValidationErrorsState>(initialState);
  const [loadingState, setLoadingState] = useState<SLLoadingStatus>(SLLoadingStatus.Idle);

  useEffect(() => {
    if (entryContext.loadingStatus === SLLoadingStatus.Resolved) {
      if (entryContext.entry) {
        const entry = entryContext.entry as ISLBatchResponse;
        setState(propToValidationErrors(entry.validationErrors));
      } else {
        setState(initialState);
      }
      setLoadingState(SLLoadingStatus.Resolved);
    } else if (entryContext.loadingStatus === SLLoadingStatus.Rejected) {
      setLoadingState(SLLoadingStatus.Rejected);
    } else {
      setLoadingState(SLLoadingStatus.Pending);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryContext.loadingStatus, entryContext.entry]);

  if (loadingState === SLLoadingStatus.Pending) {
    return <LoadingSpinner label="Loading validation errors" />;
  } else if (loadingState === SLLoadingStatus.Idle) {
    return null;
  } else if (loadingState === SLLoadingStatus.Rejected) {
    return (
      <Text
        variant="xLarge"
        style={{
          color: "var(--accent-font-color, #727272)",
          textAlign: "center"
        }}
      >
        Error while loading Batch Entry.
      </Text>
    );
  }

  return (
    <React.Fragment>
      <div className={headerStyles}>
        <div className="ms-Grid" dir="ltr">
          <DetailsList
            items={state.messages}
            groups={state.groups}
            columns={state.columns}
            ariaLabelForSelectAllCheckbox="Toggle selection for all items"
            ariaLabelForSelectionColumn="Toggle selection"
            checkButtonAriaLabel="Row checkbox"
            onRenderDetailsHeader={headerFormat}
          />
        </div>
      </div>
    </React.Fragment>
  );
};

SLBatchValidationErrors.displayName = "SLBatchValidationErrors";

export default SLBatchValidationErrors;
